/**
 * REST client for workflows (`/workflows*` on the async HTTP API) — Vera
 * U12. Backs the console's Jobs panel (Workflows sub-list) and the
 * full-page Workflow Details route. Read/manage only: creation and editing
 * happen through Vera's validating tools in chat (the panel's "Edit with
 * Vera" flow), never through this client.
 */

import { requestJson, requestNoContent } from './apiClient.ts';
import { asyncApiConfig } from '../config/async-api-config.ts';

/** The `spec/summary.json` sidecar, verbatim (the cross-repo list contract). */
export interface WorkflowSummary {
  readonly workflowId: string;
  readonly displayName: string;
  readonly description: string;
  readonly version: number;
  readonly stepCount: number;
  readonly updatedAt: string;
}

export interface WorkflowDetail {
  readonly workflow: WorkflowSummary;
  /** The raw WORKFLOW.md — also what "Download" saves. */
  readonly markdown: string;
  readonly historyVersions: readonly number[];
}

export interface WorkflowRunStep {
  readonly index: number;
  readonly title: string;
  readonly status: 'succeeded' | 'failed' | 'skipped' | 'limit-stopped';
  /** Stored output excerpt (present once the step completes). */
  readonly output?: string;
  readonly error?: string;
  readonly inputTokens?: number;
  readonly outputTokens?: number;
}

export interface WorkflowRunRecord {
  readonly runId: string;
  readonly workflowId: string;
  readonly trigger: 'manual' | `job:${string}`;
  readonly initiatedAt: string;
  /** When the executing runtime last wrote this record — the run's proof of
   *  life, refreshed at every step transition. Absent on records written
   *  before the field existed; see `runLiveness.ts` for why it matters. */
  readonly updatedAt?: string;
  readonly completedAt?: string;
  readonly status: 'running' | 'succeeded' | 'failed' | 'limit-stopped' | 'aborted';
  readonly steps: readonly WorkflowRunStep[];
  readonly error?: string;
}

function base(): string {
  return `${asyncApiConfig.baseUrl}/workflows`;
}

export async function listWorkflows(): Promise<readonly WorkflowSummary[]> {
  const body = await requestJson<{ items?: readonly WorkflowSummary[] }>(base(), {
    logTag: 'workflowsApi',
    friendlyError: 'Could not load your workflows.',
  });
  return body.items ?? [];
}

export async function getWorkflow(workflowId: string): Promise<WorkflowDetail> {
  return requestJson<WorkflowDetail>(`${base()}/${encodeURIComponent(workflowId)}`, {
    logTag: 'workflowsApi',
    friendlyError: 'Could not load that workflow.',
  });
}

export async function deleteWorkflow(workflowId: string): Promise<void> {
  await requestNoContent(`${base()}/${encodeURIComponent(workflowId)}`, {
    method: 'DELETE',
    logTag: 'workflowsApi',
    friendlyError: 'Could not delete that workflow.',
  });
}

export async function rollbackWorkflow(
  workflowId: string,
  toVersion: number,
): Promise<{ workflowId: string; version: number }> {
  return requestJson<{ workflowId: string; version: number }>(
    `${base()}/${encodeURIComponent(workflowId)}/rollback`,
    {
      method: 'POST',
      body: { toVersion },
      logTag: 'workflowsApi',
      friendlyError: 'Could not restore that version.',
    },
  );
}

export async function listWorkflowRuns(
  workflowId: string,
): Promise<readonly WorkflowRunRecord[]> {
  const body = await requestJson<{ runs?: readonly WorkflowRunRecord[] }>(
    `${base()}/${encodeURIComponent(workflowId)}/runs`,
    { logTag: 'workflowsApi', friendlyError: 'Could not load the run history.' },
  );
  return body.runs ?? [];
}
